import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, OnChanges, SimpleChanges, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import moment from 'moment';
import { Observable, Subject, Subscription, filter, forkJoin, of, takeUntil } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { WorkOrderService } from '../../../../services/workorder.service';
import { DealerState } from '../../../../../../shared/state/DealerState';
import { TranslationService } from '../../../../../../shared/translations/services/translation.service';
import { TranslationState } from '../../../../../../shared/state/TranslationState';
import { DateFormatService } from '../../../../../../shared/services/date-format.service';
import {
  BackOfficeCasingAuditTrailEntry,
  CasingHistoryRow,
  CasingHistoryView,
  CasingHistoryStage,
  CasingLifecycleStage,
  ShopFloorAuditTrailEntry
} from './casing-history.model';

@Component({
  selector: 'app-casing-history-tab',
  templateUrl: './casing-history-tab.component.html',
  styleUrls: ['./casing-history-tab.component.scss'],
  standalone: false
})
export class CasingHistoryTabComponent implements OnInit, OnDestroy, OnChanges {
  @Input() workOrderLineId!: string;
  @Input() workOrderHeaderId!: string;
  @Input() casingId: string;
  @Output() dataChanged = new EventEmitter<any>();
  @Output() hasChanges = new EventEmitter<boolean>();

  casingHistoryTableConfig: any;
  casingHistory = new MatTableDataSource<CasingHistoryRow>([]);
  private destroy$ = new Subject<void>();
  private loadSubscription?: Subscription;
  private loadRequested = false;
  loadingCasingHistory = false;
  loadErrorKeys: string[] = [];
  selectedViews: CasingHistoryView[] = ['table'];
  readonly lifecycleStages: CasingLifecycleStage[] = [
    { id: 'creation', labelKey: 'casingHistoryCreationStageText', icon: 'add_circle_outline' },
    { id: 'backOffice', labelKey: 'casingHistoryBackOfficeStageText', icon: 'edit_note' },
    { id: 'shopFloor', labelKey: 'casingHistoryShopFloorStageText', icon: 'factory' }
  ];
  stageRows: Record<CasingHistoryStage, CasingHistoryRow[]> = { creation: [], backOffice: [], shopFloor: [] };

  public route = inject(ActivatedRoute);
  public store = inject(Store);
  public workOrderService = inject(WorkOrderService);
  public translationService = inject(TranslationService);
  private dateFormatService = inject(DateFormatService);

  constructor() {
    this.workOrderLineId = this.route.snapshot.paramMap.get('workOrderLineId');
    this.workOrderHeaderId = this.route.snapshot.paramMap.get('workOrderId');
  }

  ngOnInit(): void {
    this.buildTableConfig();
    this.store
      .select(TranslationState.currentLanguageTranslations)
      .pipe(
        filter((translations) => !!translations),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.buildTableConfig());
  }

  loadData(): void {
    this.loadRequested = true;
    this.loadCasingHistory();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.loadRequested && (changes['casingId'] || changes['workOrderLineId'] || changes['workOrderHeaderId'])) {
      this.loadCasingHistory();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setViews(views: CasingHistoryView[]): void {
    this.selectedViews = views;
  }

  eventValue(row: CasingHistoryRow, column: 'sfAction' | 'user' | 'creationDate' | 'post' | 'sfPlant'): string {
    return this.casingHistoryTableConfig.columns[column].cell(row);
  }

  eventStage(row: CasingHistoryRow): CasingHistoryStage {
    if (row.source === 'SHOP_FLOOR') {
      return 'shopFloor';
    }
    return row.backOffice?.action === 'CREATE_WOL' || row.backOffice?.action === 'CREATE_WOH' ? 'creation' : 'backOffice';
  }

  private buildTableConfig(): void {
    const t = (key: string) => this.translationService.translate(key);

    this.casingHistoryTableConfig = {
      columns: {
        sfPlant: {
          header: t('workOrderLineDetailsPlantSiteText'),
          cell: (row: CasingHistoryRow) =>
            row.source === 'BACK_OFFICE' ? t('casingHistoryBackOfficeSourceText') : row.shopFloor?.plantSetupFk ?? ''
        },
        user: {
          header: t('workOrderLineDetailsUserText'),
          cell: (row: CasingHistoryRow) => (row.source === 'BACK_OFFICE' ? row.backOffice?.userId : row.shopFloor?.userFK) ?? ''
        },
        creationDate: {
          header: t('dateText'),
          cell: (row: CasingHistoryRow) => this.formatEventDate(row)
        },
        post: {
          header: t('workOrderLineDetailsPostText'),
          cell: (row: CasingHistoryRow) =>
            row.source === 'BACK_OFFICE' ? t('casingHistoryBackOfficeSourceText') : row.shopFloor?.postName ?? ''
        },
        sfAction: {
          header: t('actionText'),
          cell: (row: CasingHistoryRow) => {
            if (row.source !== 'BACK_OFFICE') {
              return row.shopFloor?.action ?? '';
            }
            const action = row.backOffice?.action ?? '';
            if (action === 'CREATE_WOL' || action === 'CREATE_WOH') {
              const key = action === 'CREATE_WOL' ? 'casingHistoryCasingCreatedText' : 'casingHistoryWorkOrderCreatedText';
              const message = t(key).replace('{identifier}', row.backOffice?.keyData ?? '');
              return row.backOffice?.btm === 'Y' ? `${message} ${t('casingHistoryFromMobileAppText')}` : message;
            }
            const details = row.backOffice?.details;
            return details ? `${action} - ${details}` : action;
          }
        }
      }
    };
  }

  private formatEventDate(row: CasingHistoryRow): string {
    if (row.sortTime === null) {
      return this.translationService.translate('casingHistoryDateUnknownText');
    }
    const date = moment(row.sortTime).local();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return `${date.format(`${this.dateFormatService.currentFormat} HH:mm:ss`)} (${timeZone}, UTC${date.format('Z')})`;
  }

  private loadCasingHistory(): void {
    this.loadSubscription?.unsubscribe();
    this.loadErrorKeys = [];
    this.casingHistory.data = [];
    this.stageRows = { creation: [], backOffice: [], shopFloor: [] };
    const dealer = this.store.selectSnapshot(DealerState.currentDealer);
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const canLoadShopFloor = !!(dealer?.id && this.workOrderHeaderId && this.workOrderLineId);
    const canLoadBackOffice = !!(dealer?.id && this.casingId);
    if (!canLoadShopFloor) {
      this.loadErrorKeys.push('casingHistoryShopFloorUnavailableText');
    }
    if (!canLoadBackOffice) {
      this.loadErrorKeys.push('casingHistoryBackOfficeUnavailableText');
    }
    if (!canLoadShopFloor && !canLoadBackOffice) {
      return;
    }

    const shopFloor$: Observable<CasingHistoryRow[]> = canLoadShopFloor
      ? this.workOrderService.fetchWolShopFloorAuditTrailInfo(dealer.id, this.workOrderHeaderId, this.workOrderLineId, localTimeZone).pipe(
          map((response) => (response?.data || []).map((entry: ShopFloorAuditTrailEntry) => this.toShopFloorRow(entry))),
          catchError((error: unknown) => {
            console.error('Failed to load shop-floor casing history', error);
            this.loadErrorKeys.push('casingHistoryShopFloorLoadErrorText');
            return of([]);
          })
        )
      : of([]);

    const backOffice$: Observable<CasingHistoryRow[]> = canLoadBackOffice
      ? this.workOrderService.fetchBoAuditTrailByCasingId(this.casingId, localTimeZone).pipe(
          map((response) => (response?.data || []).map((entry: BackOfficeCasingAuditTrailEntry) => this.toBackOfficeRow(entry))),
          catchError((error: unknown) => {
            console.error('Failed to load back-office casing history', error);
            this.loadErrorKeys.push('casingHistoryBackOfficeLoadErrorText');
            return of([]);
          })
        )
      : of([]);

    this.loadingCasingHistory = true;
    this.loadSubscription = forkJoin([shopFloor$, backOffice$])
      .pipe(
        finalize(() => (this.loadingCasingHistory = false)),
        takeUntil(this.destroy$)
      )
      .subscribe(([shopFloorRows, backOfficeRows]) => {
        this.casingHistory.data = this.sortByCreateDateDesc([...shopFloorRows, ...backOfficeRows]);
        for (const row of this.casingHistory.data) {
          this.stageRows[this.eventStage(row)].push(row);
        }
      });
  }

  private toShopFloorRow(entry: ShopFloorAuditTrailEntry): CasingHistoryRow {
    // Raw creationDate is UTC, including timestamps without an explicit timezone suffix.
    const parsed = entry?.creationDate != null ? moment.utc(entry.creationDate) : null;
    return { source: 'SHOP_FLOOR', sortTime: parsed?.isValid() ? parsed.valueOf() : null, shopFloor: entry };
  }

  private toBackOfficeRow(entry: BackOfficeCasingAuditTrailEntry): CasingHistoryRow {
    // createDt is formatted by the backend in the browser timezone using the dealer date format.
    const dateFormat = this.dateFormatService.currentFormat || 'MM/DD/YYYY';
    const parsed = entry?.createDt ? moment(entry.createDt, [`${dateFormat} HH:mm:ss`, moment.ISO_8601], true) : null;
    return { source: 'BACK_OFFICE', sortTime: parsed?.isValid() ? parsed.valueOf() : null, backOffice: entry };
  }

  private sortByCreateDateDesc(rows: CasingHistoryRow[]): CasingHistoryRow[] {
    return rows.sort((a, b) => {
      if (a.sortTime === b.sortTime) return 0;
      if (a.sortTime === null) return 1;
      if (b.sortTime === null) return -1;
      return b.sortTime - a.sortTime;
    });
  }
}
